import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { Text, Icon, Image } from '@/ui'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { TypographyStyles } from '@/styles'

const steps = [
  {
    title: 'Connect your business accounts',
    description:
      'Link your business bank accounts or credit cards so we can automatically organize your transactions.',
    duration: '5 minutes',
    image: 'https://heard-images.s3.amazonaws.com/assets/bank-yellow.svg',
  },
  {
    title: 'Tell us about your accounts',
    description:
      'Let us know if there are any special considerations around your accounts. This helps us optimize your bookkeeping.',
    duration: '5 minutes',
    image: 'https://heard-images.s3.amazonaws.com/assets/laptop-only.png',
  },
  {
    title: 'Upload past month bank statements',
    description:
      "Provide statements so our team can verify your books' accuracy, spot tax deductions, and ensure you're always audit-ready.",
    duration: '10 minutes',
    image: 'https://heard-images.s3.amazonaws.com/assets/upload-statement.png',
  },
]

export const NewObSplashScreen = () => {
  const currentUser = useReselector(getCurrentUser)

  return (
    <>
      <div className="flex justify-center bg-moss-25 rounded-2xl p-6 mb-8">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/book-celebration.svg"
          alt=""
          className="w-[180px] h-auto"
        />
      </div>

      <div className="flex flex-col lg:flex-row justify-between items-center gap-6 mb-10">
        <div className="flex flex-col gap-4 max-w-2xl text-center lg:text-left">
          <Text variant={TypographyStyles.H2}>
            Welcome to Heard
            {currentUser?.firstName ? `, ${currentUser.firstName}` : ''}!
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            We&apos;re so excited you&apos;re here. At Heard, we&apos;re on a
            mission to lighten the mental load of running your practice, so you
            can focus on what you do best.
          </Text>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-10 mb-10">
        <div className="flex flex-col gap-4 max-w-2xl">
          <Text variant={TypographyStyles.H2}>
            You&apos;re close to unlocking your practice insights
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            Your dashboard is your go-to hub for financial clarity and
            confidence. We analyze your transactions to provide instant insights
            on your business.
          </Text>
        </div>
        <div className="shadow-md rounded-lg overflow-hidden w-full max-w-sm">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/chartting.png"
            alt=""
            className="w-full object-contain"
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 mb-6 max-w-2xl">
        <Text variant={TypographyStyles.H2}>Finish account setup</Text>
        <Text variant={TypographyStyles.BODY_MD}>
          Your insights will become available once we finish setting up your
          account. These steps ensure your books are accurate, complete, and
          ready for analysis.
        </Text>
      </div>

      <div className="flex flex-col gap-6">
        {steps.map((step, index) => (
          <div
            key={step.title}
            className="flex flex-col lg:flex-row items-center gap-6 bg-stone40 p-6 rounded-lg"
          >
            <div className="flex flex-1 gap-6 items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-moss text-black rounded-full font-bold text-lg">
                {index + 1}
              </div>
              <div className="flex flex-col gap-2">
                <Text variant={TypographyStyles.H3}>{step.title}</Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="text-charcoal"
                >
                  {step.description}
                </Text>
                <div className="flex items-center text-oak">
                  <Icon icon={regular('timer')} className="mr-2" size="sm" />
                  <Text variant={TypographyStyles.BODY_XS}>
                    {step.duration}
                  </Text>
                </div>
              </div>
            </div>
            <Image
              src={step.image}
              alt="step image"
              className="w-[116px] object-contain mt-4 lg:mt-0"
            />
          </div>
        ))}
      </div>
    </>
  )
}
